import type { ComponentType } from 'react';
import { createPortal } from 'react-dom';

import { useViewportSlots } from '@/hooks/use-viewport-slots';

interface PanelManagerProps {
    /** Map of panelId → React component to render in that panel slot. */
    panels: Record<string, ComponentType>;
}

/**
 * Renders React components into panel slots in the current layout via portals.
 *
 * For each layout cell with `slotKind: 'panel'`, looks up the component by
 * `panelId` in the `panels` map and portals it into the cell's `panel` slot.
 *
 * Place as a sibling of ViewportContainer inside the VisualizationProvider tree.
 *
 * @example
 * <PanelManager panels={{ 'evar-sizing-sheet': EvarSizingSheet }} />
 */
export function PanelManager({ panels }: PanelManagerProps) {
    const viewports = useViewportSlots(['panel']);

    return (
        <>
            {viewports
                .filter((vp) => vp.slotKind === 'panel' && vp.panelId && vp.slots.panel)
                .map((vp) => {
                    const Component = panels[vp.panelId!];
                    if (!Component) return null;
                    return createPortal(<Component />, vp.slots.panel!, `panel-${vp.id}`);
                })}
        </>
    );
}
